import * as React from 'react';
import { gsap } from 'gsap';
import { IRecentSearchTile } from '../../utilities/types';
import Icons from '../../../../utilities/icons';
import RecentSearchTileHeader from './RecentSearchTileHeader';
import RecentSearchTileImg from './RecentSearchTileImg';
import TileKebabMenuItemSnackbar from '../tile-kebab-menu/TileKebabMenuItemSnackbar';
import addQueryParam from '../../utilities/addQueryParam';
import { trackEDDL, ANALYTICS_CONSTANTS, buildModuleExperience } from '../../../../utilities/analytics';
import { ITrackingPayload } from '../../../../types/analytics';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import KebabMenu, { IKebabMenuItem } from '../../../../components/KebabMenu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import KmxShareStroked from '@kmx/mui-icons/KmxShareStroked';
import Skeleton from '@mui/material/Skeleton';

interface IRecentSearchTileProps {
    linkAttrs?: { [key: string]: any };
    recentSearchTile: IRecentSearchTile;
    isLoading?: boolean;
}

const RecentSearchTile = (props: IRecentSearchTileProps) => {
    const isLoading = props.isLoading;
    const {
        title,
        link,
        newMatchCountText,
        newMatchCount,
        inventoryCount,
        inventoryCountText,
        imageUrl,
        imageAlt,
        isPartialRecentSearch,
    } = props.recentSearchTile;
    const linkAttrs = props.linkAttrs ? props.linkAttrs : {};

    const [imgLoaded, setImgLoaded] = React.useState(false);
    const [isShareSnackbarOpen, setIsShareSnackbarOpen] = React.useState<boolean>(false);

    const loadingBarRef = React.useRef(null);

    React.useEffect(() => {
        const tween = gsap.timeline({ repeat: -1 });

        if (isLoading || !imgLoaded) {
            tween.fromTo(
                loadingBarRef.current,
                { translateX: '-48px' },
                {
                    translateX: '421px',
                    ease: 'none',
                    duration: 0.9,
                }
            );
        } else {
            tween.kill();
        }
    }, [isLoading, imgLoaded]);

    const handleShareClick = async () => {
        await navigator.clipboard.writeText(`${window.location.origin}${link}`);
        setIsShareSnackbarOpen(true);
    };

    const handleTileClick = (title: string, inventoryCount: number) => {
        const payload: ITrackingPayload = {
            event: ANALYTICS_CONSTANTS.EVENTS.CLICK.RECOMMENDED_CARTILE,
            linkDetails: {
                name: String(inventoryCount),
                position: title,
            },
            module: {
                moduleExperience: buildModuleExperience('VR|Homepage|Searched Tiles'),
            },
        };
        trackEDDL(payload);
    };

    const KEBAB_MENU_ITEMS: IKebabMenuItem[] = [
        {
            text: 'Share',
            icon: <KmxShareStroked color="secondary" fontSize="small" />,
            handleClick: (e) => {
                e.preventDefault();
                handleShareClick();
                const payload: ITrackingPayload = {
                    event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                    linkDetails: {
                        name: 'share',
                        position: `Searched:${title}`,
                    },
                };
                trackEDDL(payload);
            },
            href: '#share',
        },
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                //TODO : Change this #F2F3F5 reference to background.subdued when available
                backgroundColor: '#F2F3F5',
                position: 'relative',
                width: '16.875rem',
                height: '20.3125rem',
                textAlign: 'left',
                textDecoration: 'none',
                transition: 'all 0.25s ease',
                borderRadius: 1.5,
            }}
        >
            <Box
                component="figure"
                sx={{
                    margin: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 1.5,
                }}
            >
                {(isLoading || !imgLoaded) && (
                    <Skeleton
                        data-testid={`recent-search-tile-skeleton-${title}`}
                        variant="rounded"
                        sx={{ flexGrow: 1 }}
                    />
                )}
                <Link
                    sx={{
                        position: 'relative',
                        height: '100%',
                    }}
                    underline="none"
                    href={!newMatchCount ? link : addQueryParam(link, 'newMatch=true')}
                    {...linkAttrs}
                    tabIndex={0}
                    onClick={() => {
                        handleTileClick(title, inventoryCount);
                    }}
                    data-ignore-anchor-tag-rule
                >
                    <Stack sx={{ height: '100%' }}>
                        <RecentSearchTileHeader
                            title={title}
                            newMatchCountText={newMatchCountText}
                            inventoryCount={inventoryCount}
                            inventoryCountText={inventoryCountText}
                            isPartialRecentSearch={isPartialRecentSearch}
                        />
                        <Box
                            sx={{
                                height: '5rem',
                                width: '16.875rem',
                                position: 'absolute',
                                zIndex: 1,
                                top: '5rem',
                                //TODO : Change this #F2F3F5 reference to pallette.background.subdued when available
                                background: `linear-gradient(180deg, #F2F3F5 0%, rgba(240, 241, 242, 0) 100%)`,
                            }}
                        />
                        <RecentSearchTileImg
                            src={imageUrl}
                            alt={imageAlt}
                            inventoryCount={inventoryCount}
                            handleOnLoad={() => setImgLoaded(true)}
                        />
                    </Stack>
                </Link>
            </Box>
            <Box
                sx={{
                    right: '.5rem',
                    bottom: '.5rem',
                    position: 'absolute',
                }}
            >
                <KebabMenu>
                    {KEBAB_MENU_ITEMS.map(({ handleClick, icon, text }) => (
                        <MenuItem key={text} onClick={handleClick}>
                            <ListItemIcon>{icon}</ListItemIcon>
                            <ListItemText>{text}</ListItemText>
                        </MenuItem>
                    ))}
                </KebabMenu>
            </Box>

            <TileKebabMenuItemSnackbar
                icon={Icons.share}
                isOpen={isShareSnackbarOpen}
                textLabel="Link copied to your clipboard"
                cancelAction={() => setIsShareSnackbarOpen(false)}
            />
        </Paper>
    );
};

export default RecentSearchTile;
